/* eslint-disable react/display-name */

import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import React, { FC, useEffect, useMemo, useState } from "react";
import { GridColDef } from "@material-ui/data-grid";

import BaseDataGrid from "../../../../../components/modules/BaseDataGrid";
import Loader from "../../../../../components/elements/Loader";
import SiteReportsTableToolbar from "./SiteReportsTableToolbar";
import api from "../../../../../api";
import { BrandgenCampaignPool, ReportTimeFrames } from "../../../../../types/campaignTypes";
import { RowType, transformRowValue } from "../../../../../helpers/campainRowFormatHelpers";
import { adLibFloatingNumbersFields, adLibMetricsColumns } from "../../constants";
import { useCampaignDashboardStyles } from "../../CampaignDashboardStylesContext";

export type SiteReportItem = {
  id: string;
  siteDomain: string;
  campaignName?: string | null;
  timeFrame: string;
  impressions?: number | null;
  clicks?: number | null;
  ctr?: number | null;
  cpm?: number | null;
  cpc?: number | null;
  totalSpend?: number | null;
  totalConversions?: number | null;
  totalRevenue?: number | null;
  videoViews?: number | null;
  videoComplete?: number | null;
  [key: string]: unknown;
};

type SiteReportsTableProps = {
  selectedTimeFrame: ReportTimeFrames;
  campaign: BrandgenCampaignPool;
};

const SiteReportsTable: FC<SiteReportsTableProps> = ({ selectedTimeFrame, campaign }) => {
  const classes = useCampaignDashboardStyles();
  const [reports, setReports] = useState<SiteReportItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;

    const fetchReports = async () => {
      try {
        setIsLoading(true);
        const { payload } = await api.getSiteReports(campaign.brandgenCampaignPoolId);
        const list = Array.isArray(payload) ? payload : [];
        if (!cancelled) setReports(list);
      } catch {
        if (!cancelled) setReports([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchReports();
    return () => {
      cancelled = true;
    };
  }, [campaign.brandgenCampaignPoolId]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "siteDomain",
        headerName: "Site Domain",
        width: 280,
        renderCell: ({ value }) => (
          <Tooltip title={String(value)}>
            <Typography>{String(value)}</Typography>
          </Tooltip>
        ),
      },
      {
        field: "campaignName",
        headerName: "Line Item Scope",
        width: 250,
        renderCell: ({ value }) => (
          <Tooltip title={value ? String(value) : "—"}>
            <Typography>{value ? String(value) : "—"}</Typography>
          </Tooltip>
        ),
      },
      ...adLibMetricsColumns.map(({ hide, ...col }) => col),
    ],
    [],
  );

  const filteredReports = useMemo(
    () => reports.filter((report) => report.timeFrame === selectedTimeFrame),
    [reports, selectedTimeFrame],
  );

  const rows = useMemo(() => {
    return filteredReports.map((report, index) => {
      const baseRow: Record<string, unknown> = {
        id: report.id + index,
        siteDomain: report.siteDomain ?? "N/A",
        campaignName: report.campaignName ?? "—",
      };
      for (const metric of adLibMetricsColumns) {
        const value = report[metric.field];
        const isFloat = adLibFloatingNumbersFields.includes(metric.field);
        baseRow[metric.field] =
          value !== undefined && value !== null
            ? transformRowValue(Number(value), metric.type === "FLAT", isFloat, metric.type as RowType)
            : "N/A";
      }
      return baseRow;
    });
  }, [filteredReports]);

  const isMaxRowsAmountExceeded = rows.length > 10;

  if (!isLoading && filteredReports.length === 0) {
    return null;
  }

  return (
    <div className={classes.campaignsLineItemsReportSection}>
      <div
        className={`${classes.lineItemsDashboard} ${
          isMaxRowsAmountExceeded ? classes.lineItemsDashboardFixedHeight : ""
        }`}
      >
        {isLoading ? (
          <Loader className={classes.loader} isAbsolutePosition={false} size={42} />
        ) : (
          <BaseDataGrid
            components={{ Toolbar: SiteReportsTableToolbar }}
            localeText={{ toolbarColumns: "Select Metrics" }}
            autoHeight={!isMaxRowsAmountExceeded}
            columns={columns}
            rows={rows.length ? rows : []}
          />
        )}
      </div>
    </div>
  );
};

export default SiteReportsTable;
